import React, { useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import BreadcrumbNav from "@/components/BreadcrumbNav";
import CTASection from "@/components/CTASection";
import { TESTIMONIALS, BEFORE_AFTER } from "@/data/testimonials";
import { Quote, Star, MapPin } from "lucide-react";
import { SITE } from "@/lib/site";

const STAR_VALUES = [1, 2, 3, 4, 5];
const BREADCRUMBS = [{ label: "Home", to: "/" }, { label: "Testimonials" }];
const FILLED_STAR = { fill: "#d4a24c", color: "#d4a24c" };
const EMPTY_HERO_STAR = { color: "#6b6b6b" };
const EMPTY_CARD_STAR = { color: "#d1d5db" };

const RATING_FILTERS = [
  { key: "all", label: "All Reviews" },
  { key: "5", label: "5-Star" },
  { key: "4", label: "4-Star" },
];

function buildAggregateRatingJsonLd() {
  const reviewCount = TESTIMONIALS.length;
  const avg = TESTIMONIALS.reduce((acc, t) => acc + (t.rating || 5), 0) / reviewCount;
  return {
    "@context": "https://schema.org",
    "@type": ["GeneralContractor", "RoofingContractor", "LocalBusiness"],
    name: SITE.name,
    url: SITE.domain,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: avg.toFixed(1),
      reviewCount: reviewCount,
      bestRating: "5",
      worstRating: "1",
    },
    review: TESTIMONIALS.map((t) => ({
      "@type": "Review",
      reviewRating: { "@type": "Rating", ratingValue: t.rating || 5, bestRating: "5" },
      author: { "@type": "Person", name: t.name },
      reviewBody: t.quote,
      itemReviewed: { "@type": "Service", name: t.project, areaServed: t.location },
    })),
  };
}

function computeStats(reviews) {
  const aggregate = reviews.length;
  const totalScore = reviews.reduce((acc, t) => acc + (t.rating || 5), 0);
  const avgRating = (totalScore / aggregate).toFixed(1);
  const fiveCount = reviews.filter((t) => (t.rating || 5) === 5).length;
  const fourCount = reviews.filter((t) => (t.rating || 5) === 4).length;
  return { aggregate, avgRating, fiveCount, fourCount };
}

function RatingDistributionBar({ label, count, total }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3 text-xs" data-testid={`rating-bar-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="w-14 tracking-widest uppercase text-gray-400">{label}</div>
      <div className="flex-1 h-1.5 bg-br-line/40 rounded overflow-hidden">
        <div className="h-full bg-br-bronze-2 transition-[width] duration-500" style={{ width: `${pct}%` }} />
      </div>
      <div className="w-12 text-right tabular-nums text-gray-300">{count}</div>
    </div>
  );
}

export default function Testimonials() {
  const [ratingFilter, setRatingFilter] = useState("all");

  const stats = useMemo(() => computeStats(TESTIMONIALS), []);

  const visibleReviews = useMemo(() => {
    if (ratingFilter === "all") return TESTIMONIALS;
    const target = Number(ratingFilter);
    return TESTIMONIALS.filter((t) => (t.rating || 5) === target);
  }, [ratingFilter]);

  const handleFilterChange = useCallback((key) => setRatingFilter(key), []);

  const { aggregate, avgRating, fiveCount, fourCount } = stats;

  return (
    <>
      <SEO
        title="Client Testimonials & Project Stories | Black Ridge Construction Austin TX"
        description={`Read ${aggregate} homeowner testimonials and project stories from Black Ridge Construction across Austin, Round Rock, Cedar Park, Georgetown, Kyle, Buda, and Central Texas. ${avgRating}/5 average rating.`}
        jsonLd={buildAggregateRatingJsonLd()}
      />

      <section className="bg-br-black text-white">
        <div className="container-br py-12 lg:py-16">
          <BreadcrumbNav dark items={BREADCRUMBS} />
          <div className="grid lg:grid-cols-12 gap-10 mt-5 items-start">
            <div className="lg:col-span-7">
              <div className="br-eyebrow text-br-bronze-2">Client Feedback</div>
              <h1 className="br-headline text-4xl sm:text-5xl mt-3">What Austin & Central Texas Homeowners Are Saying</h1>
              <p className="mt-4 text-gray-300">
                Real homeowner feedback and project stories from across our Central Texas service area — Austin, Round Rock, Cedar Park, Georgetown, Pflugerville, Kyle, Buda, Lakeway, Dripping Springs, Wimberley, and beyond. We publish both five-star and four-star reviews so you see the full picture, not a filtered highlight reel.
              </p>
              <div className="mt-5 flex items-center gap-3 text-sm">
                <div className="flex items-center gap-1" aria-label={`${avgRating} out of 5 stars`}>
                  {STAR_VALUES.map((i) => (
                    <Star key={i} size={16} style={i <= Math.round(avgRating) ? FILLED_STAR : EMPTY_HERO_STAR} />
                  ))}
                </div>
                <div className="text-gray-300" data-testid="testimonials-summary">
                  <span className="font-bold text-white">{avgRating}</span> / 5 across <span className="font-bold text-white">{aggregate}</span> client experiences
                </div>
              </div>
            </div>
            <div className="lg:col-span-5 lg:justify-self-end w-full lg:max-w-sm bg-br-charcoal border border-br-line rounded-md p-5">
              <div className="text-[10px] tracking-[0.3em] text-br-bronze-2 mb-3">RATING BREAKDOWN</div>
              <div className="space-y-2">
                <RatingDistributionBar label="5 Star" count={fiveCount} total={aggregate} />
                <RatingDistributionBar label="4 Star" count={fourCount} total={aggregate} />
              </div>
              <div className="mt-4 pt-4 border-t border-br-line text-[11px] text-gray-400 leading-relaxed">
                We do not solicit edited reviews. Four-star experiences highlight where we can still improve — usually scheduling, lead times, or supplier delays.
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-br-paper border-b border-gray-200">
        <div className="container-br py-5 flex flex-wrap items-center gap-3" data-testid="testimonials-filter-bar">
          <span className="text-[11px] tracking-[0.3em] text-gray-500">FILTER BY RATING:</span>
          {RATING_FILTERS.map((opt) => {
            const isActive = ratingFilter === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => handleFilterChange(opt.key)}
                aria-pressed={isActive}
                data-testid={`filter-${opt.key}`}
                className={`px-4 py-1.5 text-xs uppercase tracking-widest rounded border transition focus:outline-none focus-visible:ring-2 focus-visible:ring-br-bronze focus-visible:ring-offset-1 ${
                  isActive
                    ? "bg-br-black text-white border-br-black"
                    : "bg-white text-gray-700 border-gray-300 hover:border-br-bronze hover:text-br-bronze"
                }`}
              >
                {opt.label}
              </button>
            );
          })}
          <span className="ml-auto text-xs text-gray-500" data-testid="testimonials-visible-count">
            Showing <span className="font-bold text-gray-900">{visibleReviews.length}</span> of {aggregate}
          </span>
        </div>
      </section>

      <section className="section-pad bg-br-paper">
        <div className="container-br">
          {visibleReviews.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded p-8 text-center text-sm text-gray-600" data-testid="testimonials-empty-state">
              No reviews match this filter yet.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="testimonials-grid">
              {visibleReviews.map((t) => (
                <article
                  key={t.id}
                  className="p-6 bg-white border border-gray-200 rounded flex flex-col hover:border-br-bronze transition"
                  itemScope
                  itemType="https://schema.org/Review"
                  data-testid={`testimonial-card-${t.id}`}
                >
                  <Quote size={22} className="text-br-bronze mb-3" />
                  <div className="flex items-center gap-0.5 mb-3" aria-label={`${t.rating} out of 5 stars`}>
                    {STAR_VALUES.map((i) => (
                      <Star key={i} size={14} style={i <= t.rating ? FILLED_STAR : EMPTY_CARD_STAR} />
                    ))}
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed flex-1" itemProp="reviewBody">{`"${t.quote}"`}</p>
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div
                      className="font-display tracking-widest uppercase text-sm text-gray-900"
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      <span itemProp="name">{t.name}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-0.5">
                      <MapPin size={11} className="text-br-bronze" />
                      <span>{t.location}</span>
                    </div>
                    <Link
                      to={`/services/${t.service}`}
                      className="mt-1 inline-block text-[11px] uppercase tracking-widest font-bold text-br-bronze hover:underline"
                    >
                      {t.project} →
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="section-pad bg-white">
        <div className="container-br">
          <div className="max-w-2xl mb-8">
            <div className="br-eyebrow">Project Stories</div>
            <h2 className="br-headline text-3xl sm:text-4xl mt-3">Before & After From the Service Area</h2>
            <p className="mt-3 text-gray-700">Project highlights from across the Austin and Central Texas area.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {BEFORE_AFTER.map((b) => (
              <div key={b.id} className="bg-br-paper rounded border border-gray-200 overflow-hidden">
                <div className="grid grid-cols-2">
                  <div className="relative">
                    <img src={b.before} alt={`${b.title} before`} className="w-full h-44 object-cover" loading="lazy" />
                    <div className="absolute top-2 left-2 bg-black/70 text-white text-[10px] tracking-widest uppercase px-2 py-0.5">Before</div>
                  </div>
                  <div className="relative">
                    <img src={b.after} alt={`${b.title} after`} className="w-full h-44 object-cover" loading="lazy" />
                    <div className="absolute top-2 left-2 bg-br-bronze text-white text-[10px] tracking-widest uppercase px-2 py-0.5">After</div>
                  </div>
                </div>
                <div className="p-4">
                  <div className="font-display tracking-wider uppercase text-sm">{b.title}</div>
                  <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                    <MapPin size={11} className="text-br-bronze" />
                    {b.location}
                  </div>
                  <div className="text-xs text-gray-600 mt-2">{b.caption}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        title="Become Our Next Project Story"
        subtitle="Talk to a real general contractor about your roofing, remodel, or maintenance project across Austin and Central Texas."
      />
    </>
  );
}
